import { useState } from 'react';
import { Link } from 'react-router';
import { LinkButton } from '../../components/linkButton.tsx';
import { Protocol } from '../../types/sharedTypeImpl.ts';
import { formatDatetime } from '../../utils/fieldFormats.ts';
import { ProtocolContextMenu, ProtocolContextMenuState } from './protocolContextMenu.tsx';


interface Props {
    protocols: Protocol[] | undefined;
    onProtocolSetDisabled: (protocol: Protocol, isEnabled: boolean) => void;
    onProtocolDraftDelete: (protocol: Protocol) => void;
    onProtocolCopy: (protocol: Protocol) => void;
    onSearchTextChange: (searchText: string) => void;
}

export function ProtocolTable({
    protocols,
    onProtocolSetDisabled,
    onProtocolDraftDelete,
    onProtocolCopy,
    onSearchTextChange,
}: Props) {
    const [menuProps, setMenuProps] = useState<ProtocolContextMenuState | undefined>(undefined);

    function handleMenuOpen(e: React.MouseEvent, protocol: Protocol) {
        e.preventDefault();
        setMenuProps({ protocol, screenCoords: { x: e.clientX, y: e.clientY } });
    }


    return (
        <>
            <table className='protocol-table'>
                <thead>
                    <tr>
                        <th>Part number</th>
                        <th>Program name</th>
                        <th style={{ minWidth: '85px' }}>Positions</th>
                        <th style={{ minWidth: '110px' }}>Version</th>
                        <th style={{ width: '220px' }}>Customer</th>
                        <th style={{ minWidth: '160px' }}>Create time</th>
                        <th style={{ minWidth: '70px' }}>Status</th>
                        <th style={{ minWidth: '50px' }} />
                        <th style={{ minWidth: '110px' }} />
                        <th style={{ minWidth: '70px' }} />
                    </tr>
                </thead>
                <tbody>
                    {protocols?.map(it => {

                        return <tr key={it._id} className={getRowClass(it)}>
                            <td className='part-number'>
                                <CellWithSearchLink text={it.partDisplayName} onSearchTextChange={onSearchTextChange} />
                            </td>
                            <td className='program'>
                                <CellWithSearchLink text={it.programs?.[0] ?? ''} onSearchTextChange={onSearchTextChange} />
                            </td>
                            <td>{it.positions?.length ?? ''}</td>
                            <td>{it.version}</td>
                            <td className='customer'>{it.customer}</td>
                            <td>{formatDatetime(it.createTime)}</td>
                            <td><Status protocol={it} /></td>
                            <td><ViewButton protocol={it} /></td>
                            <td><EditButton protocol={it} /></td>
                            <td><LinkButton onClick={(e) => { handleMenuOpen(e, it) }}>Options</LinkButton></td>
                        </tr >
                    })}
                </tbody>
            </table >

            {menuProps && <ProtocolContextMenu
                protocol={menuProps.protocol}
                screenCoords={menuProps.screenCoords}
                onEnableSelected={(protocol) => onProtocolSetDisabled(protocol, false)}
                onDisableSelected={(protocol) => onProtocolSetDisabled(protocol, true)}
                onProtocolDraftDelete={onProtocolDraftDelete}
                onCopySelected={onProtocolCopy}
                onClose={() => setMenuProps(undefined)}
            />}
        </>
    );
}


function CellWithSearchLink({
    text,
    onSearchTextChange
}: { text: string, onSearchTextChange: (searchText: string) => void }) {
    return (
        <span className='link-button--non-intrusive' onClick={() => onSearchTextChange(text)}>
            {text}
        </span>
    );
}

function getRowClass(protocol: Protocol): string | undefined {
    return protocol.status === 'rejected' ? 'highlight--issue'
        : protocol.status === 'review' ? 'highlight--selected'
            : undefined;
}

function Status({ protocol }: { protocol: Protocol }) {
    const statusText = protocol.status === 'draft' ? 'Draft'
        : protocol.status === 'rejected' ? 'Rejected'
            : protocol.status === 'review' ? 'Review'
                : null;
    const disabledText = protocol.isDisabled ? 'Disabled' : null;
    const finalText = [statusText, disabledText].filter(it => it).join(', ');

    return <>{finalText}</>;
}

function ViewButton({ protocol }: { protocol: Protocol }) {
    const returnTo = { returnTo: location.pathname + location.search };
    const path = `/protocol-view?id=${protocol._id}&mode=view`;
    return <Link to={path} state={returnTo} className='a__button'>View</Link>;
}

function EditButton({ protocol }: { protocol: Protocol }) {
    const continueDraft = protocol.status === 'draft' || protocol.status === 'rejected';
    const reviewProtocol = protocol.status === 'review';

    const returnTo = { returnTo: location.pathname + location.search };
    const path = continueDraft
        ? `/protocol-view?id=${protocol._id}&mode=edit`
        : reviewProtocol
            ? `/protocol-view?id=${protocol._id}&mode=review`
            : `/protocol-view?baseProtocolId=${protocol._id}&mode=edit`;
    const text = continueDraft ? 'Continue' : reviewProtocol ? 'Review' : 'New version';

    return <Link to={path} state={returnTo} className='a__button'>{text}</Link>;
}
